import { Avatar, Collapse, Spin } from "antd";
import {
  TagOutlined,
  HistoryOutlined,
  UserOutlined,
  EditOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useChatStore } from "@stores/chatStore";
import { useAppSelector } from "@stores/redux/store";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import FacebookIcon from "@assets/facebook-icon.svg";
import ZaloIcon from "@assets/zalo-icon.svg";
import ShopeeIcon from "@assets/shopee-icon.svg";
import { CHANNELS, getTagColor } from "@common";
import type { ChannelType } from "@interfaces/chat.interface";
import { getConversationHistory } from "@api/services/agents";
import { getAvatarGradient, getAvatarLetter } from "@utils/avatarUtils";
import styles from "./styles.module.less";
import classNames from "classnames";
import dayjs from "dayjs";

interface RecentActivity {
  timestamp: string | Date;
  description: string;
  type?: "assign" | "tag" | "update" | "other";
}

const getActivityIcon = (type?: string) => {
  switch (type) {
    case "assign":
      return <UserOutlined />;
    case "tag":
      return <TagOutlined />;
    case "update":
      return <EditOutlined />;
    default:
      return <ClockCircleOutlined />;
  }
};

const formatActivityTime = (timestamp: string | Date): string => {
  return dayjs(timestamp).format("DD/MM/YYYY, HH:mm");
};

const getPlatformInfo = (channelType: ChannelType) => {
  switch (channelType) {
    case CHANNELS.FACEBOOK:
      return {
        label: "Facebook",
        icon: FacebookIcon,
      };
    case CHANNELS.ZALO:
      return {
        label: "Zalo",
        icon: ZaloIcon,
      };
    case CHANNELS.SHOPEE:
      return {
        label: "Shopee",
        icon: ShopeeIcon,
      };
    default:
      return {
        label: "Unknown",
        icon: null,
      };
  }
};

const getPlatformString = (channelType: ChannelType): string => {
  switch (channelType) {
    case CHANNELS.FACEBOOK:
      return "Facebook";
    case CHANNELS.ZALO:
      return "Zalo";
    case CHANNELS.SHOPEE:
      return "Shopee";
    default:
      return "Facebook";
  }
};

const detectActivityType = (description: string): "assign" | "tag" | "update" | "other" => {
  const lowerDesc = description.toLowerCase();
  if (lowerDesc.includes("gán") || lowerDesc.includes("chuyển") || lowerDesc.includes("gỡ")) {
    return "assign";
  }
  if (lowerDesc.includes("tag")) {
    return "tag";
  }
  if (lowerDesc.includes("cập nhật") || lowerDesc.includes("thay đổi")) {
    return "update";
  }
  return "other";
};

export const FanpageActiveSidebar = () => {
  const selectedChannel = useChatStore((state) => state.selectedChannel);
  const selectedFanpageName = useChatStore(
    (state) => state.selectedFanpageName
  );
  const selectedConversationId = useChatStore(
    (state) => state.selectedConversationId
  );
  const conversations = useChatStore((state) => state.conversations);
  const { pages: facebookPages } = useAppSelector((state) => state.fanpage);
  const agentsByConversation = useAppSelector(
    (state) => state.agents.agentsByConversation
  );

  const activePage = useMemo(() => {
    if (!selectedFanpageName || !facebookPages.length) return null;
    return (
      facebookPages.find((page) => page.name === selectedFanpageName) || null
    );
  }, [selectedFanpageName, facebookPages]);

  const activePageId = useMemo(() => {
    return activePage?.id || null;
  }, [activePage]);

  const activeConversation = useMemo(() => {
    if (!selectedConversationId) return null;
    return (
      conversations.find((conv) => conv.id === selectedConversationId) || null
    );
  }, [selectedConversationId, conversations]);

  const conversationExternalId = useMemo(() => {
    return activeConversation?.lastMessage?.conversationId || null;
  }, [activeConversation]);

  const activeAgent = useMemo(() => {
    if (!conversationExternalId) return null;
    return agentsByConversation[conversationExternalId] || null;
  }, [conversationExternalId, agentsByConversation]);

  const conversationTagsMap = useChatStore((state) => state.conversationTagsMap);
  
  const tags = useMemo(() => {
    if (!conversationExternalId) return [];
    return conversationTagsMap[conversationExternalId] || [];
  }, [conversationExternalId, conversationTagsMap]);

  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalActivities, setTotalActivities] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const refreshTrigger = useChatStore((state) => state.refreshConversationHistoryTrigger);
  const activitiesListRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);

  const fetchActivities = useCallback(async (offset = 0, append = false) => {
    if (!conversationExternalId || !selectedChannel) {
      setRecentActivities([]);
      setTotalActivities(0);
      setCurrentOffset(0);
      return;
    }

    const platform = getPlatformString(selectedChannel);
    if (platform === "Facebook" && !activePageId) {
      setRecentActivities([]);
      setTotalActivities(0);
      setCurrentOffset(0);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoadingActivities(true);
    }

    try {
      const response = await getConversationHistory({
        externalId: conversationExternalId,
        platform,
        limit: 20,
        offset,
        pageId: activePageId || undefined,
      });

      const mappedActivities: RecentActivity[] = response.histories.map((history) => ({
        timestamp: new Date(history.performedAt),
        description: history.description,
        type: detectActivityType(history.description),
      }));

      if (append) {
        setRecentActivities((prev) => [...prev, ...mappedActivities]);
      } else {
        setRecentActivities(mappedActivities);
      }

      setTotalActivities(response.total);
      setCurrentOffset(offset + mappedActivities.length);
    } catch (error) {
      console.error("Error fetching conversation history:", error);
      if (!append) {
        setRecentActivities([]);
        setTotalActivities(0);
        setCurrentOffset(0);
      }
    } finally {
      setLoadingActivities(false);
      setLoadingMore(false);
    }
  }, [conversationExternalId, selectedChannel, activePageId]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || loadingMore || recentActivities.length >= totalActivities) {
      return;
    }

    isLoadingMoreRef.current = true;
    await fetchActivities(currentOffset, true);
    isLoadingMoreRef.current = false;
  }, [fetchActivities, currentOffset, loadingMore, recentActivities.length, totalActivities]);

  useEffect(() => {
    setCurrentOffset(0);
    fetchActivities(0, false);
    if (activitiesListRef.current) {
      activitiesListRef.current.scrollTop = 0;
    }
  }, [fetchActivities, refreshTrigger]);

  const hasMore = recentActivities.length < totalActivities;

  useEffect(() => {
    const listContainer = activitiesListRef.current;
    if (!listContainer || !hasMore || loadingMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = listContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

      if (isNearBottom && !isLoadingMoreRef.current && !loadingMore) {
        handleLoadMore();
      }
    };

    listContainer.addEventListener("scroll", handleScroll);
    return () => {
      listContainer.removeEventListener("scroll", handleScroll);
    };
  }, [hasMore, loadingMore, handleLoadMore]);


  const platformInfo = getPlatformInfo(selectedChannel);

  return (
    <div className={styles.fanpageActiveSidebar}>
      <div className={classNames(styles.scrollableContent, "scroll-thin")}>
        {activeConversation ? (
        <div className={styles.content}>
          <div className={styles.userSection}>
            {activeConversation.avatar ? (
              <Avatar
                src={activeConversation.avatar}
                size={64}
                className={styles.userAvatar}
              />
            ) : (
              <Avatar
                size={64}
                className={styles.userAvatar}
                style={{
                  background: getAvatarGradient(activeConversation.name?.charAt(0) || "A"),
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "24px",
                }}
              >
                {getAvatarLetter(activeConversation.name)}
              </Avatar>
            )}
            <div className={styles.userName}>{activeConversation.name}</div>
            {activePage && (
              <div className={styles.pageInfo}>
                {platformInfo.icon && (
                  <img
                    src={platformInfo.icon}
                    alt={platformInfo.label}
                    className={styles.platformIcon}
                  />
                )}
                <span className={styles.pageName}>{activePage.name}</span>
              </div>
            )}
          </div>

          <div className={styles.metaSection}>
            {activeAgent && (
              <div className={styles.agentSection}>
                <Avatar
                  src={activeAgent.avatarUrl}
                  size={16}
                  className={styles.agentAvatar}
                >
                  {activeAgent.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <span className={styles.agentName}>{activeAgent.name}</span>
              </div>
            )}
            {tags.length > 0 && (
              <div className={styles.tagsSection}>
                <span className={styles.tagsSectionTitle}>Tags:</span>
                <div className={styles.tagsList}>
                  {tags.map((tag) => {
                    const color = getTagColor(tag.id);
                    return (
                      <div
                        key={tag.id}
                        className={classNames(
                          styles.tag,
                          styles[`tag-${color}`]
                        )}
                      >
                        <TagOutlined /> {tag.tagName}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : activePage ? (
        <div className={styles.content}>
          <div className={styles.pageSection}>
            <Avatar
              src={
                "picture" in activePage &&
                activePage.picture &&
                typeof activePage.picture === "object" &&
                "data" in activePage.picture
                  ? (activePage.picture as { data?: { url?: string } }).data
                      ?.url
                  : undefined
              }
              size={64}
              className={styles.pageAvatar}
            >
              {activePage.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <div className={styles.pageName}>{activePage.name}</div>
            {platformInfo.icon && (
              <div className={styles.platformInfo}>
                <img
                  src={platformInfo.icon}
                  alt={platformInfo.label}
                  className={styles.platformIcon}
                />
                <span>{platformInfo.label}</span>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <Collapse
        items={[
          {
            key: "activities",
            label: (
              <span className={styles.collapseLabel}>
                <HistoryOutlined /> Hoạt động gần đây
              </span>
            ),
            children: (
              <div
                ref={activitiesListRef}
                className={styles.activitiesList}
              >
                {loadingActivities ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Spin />
                  </div>
                ) : recentActivities.length > 0 ? (
                  <>
                    {recentActivities.map((activity, index) => (
                      <div key={index} className={styles.activityItem}>
                        <div
                          className={classNames(
                            styles.activityIcon,
                            activity.type && styles[`activityIcon-${activity.type}`]
                          )}
                        >
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className={styles.activityContent}>
                          <div className={styles.activityDescription}>
                            {activity.description}
                          </div>
                          <div className={styles.activityTime}>
                            {formatActivityTime(activity.timestamp)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {loadingMore && (
                      <div style={{ textAlign: "center", padding: "12px" }}>
                        <Spin size="small" />
                      </div>
                    )}
                  </>
                ) : (
                  <div className={styles.emptyActivities}>
                    <HistoryOutlined className={styles.emptyActivitiesIcon} />
                    <div className={styles.emptyActivitiesText}>
                      Chưa có hoạt động nào
                    </div>
                  </div>
                )}
              </div>
            ),
            showArrow: false,
          },
        ]}
        defaultActiveKey={["activities"]}
        className={styles.collapse}
        bordered={false}
      />
      </div>
    </div>
  );
};
